import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { fetchEventDetails } from "../../store/slices/eventSlice";
import { clearTicketState } from "../../store/slices/ticketSlice";
import { createPaymentPreference } from "../../store/slices/paymentSlice";
import { isEventPast } from "../../utils/date";
import { buildApiUrl, showMissingApiUrlAlert } from "../../utils/apiConfig";
import { getPublicEventTicketPricing } from "../../utils/ticketPricing";
import ShareMenu from "./ShareMenu";
import {
  Calendar,
  MapPin,
  Info,
  Clock,
  Ticket,
  ExternalLink,
  ChevronRight,
  ShieldCheck,
  Store,
  Navigation
} from "lucide-react";

const formatEventDates = (event) => {
  const rawDates = Array.isArray(event?.dates) && event.dates.length > 0 ? event.dates : [event?.date];
  return [...new Set(rawDates.filter(Boolean).map((value) => new Date(value).toISOString()))]
    .sort()
    .map((value) => new Date(value).toLocaleDateString("es-AR", { timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long' }))
    .join(' y ');
};

const EventDetails = () => {
  const { slug, id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [redirecting, setRedirecting] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('detalles');
  const [selectedTicketType, setSelectedTicketType] = useState(null);

  const auth = useSelector((state) => state.auth);
  const { eventDetails: event, loading, error } = useSelector(
    (state) => state.events
  );
  const ticketState = useSelector((state) => state.tickets);

  const refreshEventDetails = useCallback(() => {
    if (slug && id) dispatch(fetchEventDetails({ slug, id }));
  }, [dispatch, slug, id]);

  useEffect(() => {
    refreshEventDetails();
    dispatch(clearTicketState());
  }, [dispatch, refreshEventDetails]);

  useEffect(() => {
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') {
        refreshEventDetails();
      }
    };

    const interval = window.setInterval(refreshWhenVisible, 15000);
    window.addEventListener('focus', refreshEventDetails);
    document.addEventListener('visibilitychange', refreshWhenVisible);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshEventDetails);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [refreshEventDetails]);

  // Set default ticket type when event loads
  useEffect(() => {
    if (event?.ticketTypes?.length > 0) {
      const current = selectedTicketType
        ? event.ticketTypes.find(t => t._id === selectedTicketType._id)
        : null;
      const available = event.ticketTypes.find(t => (t.totalQuantity - t.soldQuantity) > 0);
      const nextTicketType = current || available || event.ticketTypes[0];

      if (!selectedTicketType || selectedTicketType._id !== nextTicketType._id || selectedTicketType.soldQuantity !== nextTicketType.soldQuantity) {
        setSelectedTicketType(nextTicketType);
      }
    }
  }, [event, selectedTicketType]);

  const isPast = useMemo(() => {
    return isEventPast(event?.date);
  }, [event]);
  const isInvitationOnly = event?.clientId?.billingModel === 'subscription';
  const getTicketPricing = (basePrice, selectedQuantity = 1) => getPublicEventTicketPricing({
    basePrice,
    quantity: selectedQuantity,
    organizationType: event?.clientId?.organizationType,
    payer: event?.clientId?.commissionConfig?.payer
  });

  const handlePurchase = () => {
    if (isPast || isInvitationOnly) return;
    if (!auth.isAuthenticated) {
      const googleLoginUrl = buildApiUrl(`/api/auth/google?slug=${encodeURIComponent(slug || '')}`);
      if (!googleLoginUrl) {
        showMissingApiUrlAlert();
        return;
      }
      window.location.href = googleLoginUrl;
    } else {
      setRedirecting(true);
      const rrppId = sessionStorage.getItem(`rrpp_${slug}`);
      const paymentData = {
        userId: auth.user.id,
        eventId: event._id,
        quantity: quantity,
        rrppId: rrppId,
        ticketTypeId: selectedTicketType?._id // Send selected ticket type ID
      };

      dispatch(createPaymentPreference({ slug, paymentData }))
        .unwrap()
        .then((response) => {
          let seconds = 3;
          const interval = setInterval(() => {
            seconds -= 1;
            setCountdown(seconds);
            if (seconds === 0) {
              clearInterval(interval);
              window.location.href = response.init_point;
            }
          }, 1000);
        })
        .catch((err) => {
          console.error("Error al crear la preferencia de pago:", err);
          setRedirecting(false);
        });
    }
  };

  // Helper para construir la dirección completa
  const fullAddress = useMemo(() => {
    if (event?.address && event?.city) {
      return `${event.address}, ${event.city}, ${event.province || ''}, ${event.country || ''}`;
    }
    return event?.location || '';
  }, [event]);

  const googleMapsUrl = useMemo(() => {
    const key = process.env.REACT_APP_GOOGLE_MAPS_KEY;
    if (!fullAddress || !key) return null;
    return `https://www.google.com/maps/embed/v1/place?key=${key}&q=${encodeURIComponent(fullAddress)}`;
  }, [fullAddress]);

  // Si no hay API KEY, usamos el iframe genérico de búsqueda para que siempre funcione algo
  const fallbackMapsUrl = useMemo(() => {
    if (!fullAddress) return null;
    return `https://maps.google.com/maps?q=${encodeURIComponent(fullAddress)}&t=&z=15&ie=UTF8&iwloc=&output=embed`;
  }, [fullAddress]);

  if (loading || !event) return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-background">
      <div className="w-12 h-12 border-4 border-primary-orange/20 border-t-primary-orange rounded-full animate-spin"></div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="bg-red-500/10 border border-red-500/20 p-8 rounded-3xl text-center max-w-md w-full">
        <p className="text-red-400 font-bold mb-2">Ops! No pudimos cargar el evento</p>
        <p className="text-red-400/60 text-sm mb-6">{error}</p>
        <button onClick={() => navigate(-1)} className="bg-white/10 px-6 py-3 rounded-xl text-white font-bold hover:bg-white/20 transition">Volver atrás</button>
      </div>
    </div>
  );

  const cover = event.coverUrl || event.image || event.coverImageUrl;
  const placeholder = "/images/fotos/placeholder.svg";

  return (
    <div className="min-h-screen bg-background relative overflow-hidden pb-24 md:pb-12">
      {/* BACKGROUND ATMOSPHERIC */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div
          className="absolute inset-0 opacity-20 blur-[120px]"
          style={{
            backgroundImage: `url(${cover || placeholder})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-[#121212]/80" />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-primary-orange/10 blur-[150px] rounded-full" />
      </div>

      <div className="relative z-10 container mx-auto px-4 max-w-7xl pt-8 md:pt-16">
        <div className="flex flex-col lg:flex-row gap-12 lg:items-start">

          {/* LEFT COLUMN: POSTER (Sticky en desktop) */}
          <div className="hidden lg:block lg:w-[450px] lg:sticky lg:top-24">
            <div className="aspect-[3/4] rounded-[40px] overflow-hidden shadow-[0_32px_64px_-16px_rgba(0,0,0,0.7)] border border-white/10 group relative">
              <img
                src={cover || placeholder}
                alt={event.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/80 to-transparent flex items-end p-8">
                <div className="w-full bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/20 flex items-center justify-between">
                  <div>
                    <p className="text-[10px] font-bold text-white/50 uppercase tracking-[2px]">Organiza</p>
                    <p className="text-sm font-bold text-white">{event.clientId?.nombre || "Passar"}</p>
                  </div>
                  <Store className="text-primary-orange" size={20} />
                </div>
              </div>
            </div>
          </div>

          {/* MOBILE HERO IMAGE */}
          <div className="lg:hidden w-full -mx-4 -mt-8 mb-8 relative">
            <div className="aspect-video w-full overflow-hidden">
              <img
                src={cover || placeholder}
                alt={event.title}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
          </div>

          {/* RIGHT COLUMN: ACTION & TABS */}
          <div className="flex-1 space-y-8">
            {/* Header info */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className={`px-3 py-1 rounded-full border text-[10px] font-black uppercase tracking-widest ${isPast ? 'bg-white/5 border-white/10 text-white/40' : 'bg-primary-orange/20 border-primary-orange/30 text-primary-orange'}`}>
                  {isPast ? "Evento Finalizado" : isInvitationOnly ? "Por invitación" : event.price === 0 ? "Gratis" : "Venta Activa"}
                </div>
                <div className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-white/40 text-[10px] font-black uppercase tracking-widest">
                  Evento Presencial
                </div>
              </div>
              <h1 className="text-4xl md:text-6xl font-black text-white mb-6 leading-tight pr-12 relative">
                {event.title}
                <div className="absolute top-2 right-0">
                  <ShareMenu slug={slug} event={event} buttonClassName="p-3 bg-white/5 rounded-full hover:bg-white/10" />
                </div>
              </h1>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-surface/50 backdrop-blur-md rounded-3xl p-5 border border-white/5 flex items-center gap-4 transition-all hover:bg-white/5">
                  <div className="w-12 h-12 rounded-2xl bg-primary-orange/10 flex items-center justify-center text-primary-orange">
                    <Calendar size={24} />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-white/30 uppercase tracking-[2px]">Fecha</p>
                    <p className="text-white font-bold leading-tight capitalize">
                      {formatEventDates(event)}
                    </p>
                  </div>
                </div>
                <div className="bg-surface/50 backdrop-blur-md rounded-3xl p-5 border border-white/5 flex items-center gap-4 transition-all hover:bg-white/5">
                  <div className="w-12 h-12 rounded-2xl bg-primary-orange/10 flex items-center justify-center text-primary-orange">
                    <Clock size={24} />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-white/30 uppercase tracking-[2px]">Horario</p>
                    <p className="text-white font-bold leading-tight">
                      {event.startTime ? (
                        <>
                          Apertura {event.startTime} HS {event.endTime ? `- Cierre ${event.endTime} HS` : ''}
                        </>
                      ) : (
                        `Apertura ${new Date(event.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} HS`
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* TICKETS SECTION */}
            <div className="bg-surface rounded-3xl border border-white/5 p-6 md:p-8 shadow-2xl relative overflow-hidden bg-gradient-to-br from-surface to-surface/80">
              <div className="absolute top-0 right-0 p-8 opacity-[0.03] pointer-events-none">
                <Ticket size={120} />
              </div>
              <h2 className="text-xl font-bold text-white mb-8 flex items-center gap-2">
                <Ticket size={24} className="text-primary-orange" />
                {isInvitationOnly ? 'Acceso por invitación' : 'Tickets Disponibles'}
              </h2>

              {isInvitationOnly ? (
                <div className="relative bg-white/[0.03] border border-white/10 rounded-[28px] p-6 md:p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-primary-orange/10 text-primary-orange flex items-center justify-center shrink-0">
                      <ShieldCheck size={24} />
                    </div>
                    <div>
                      <h3 className="text-xl font-black text-white mb-2">Este evento no tiene venta pública</h3>
                      <p className="text-white/45 text-sm font-medium leading-relaxed">
                        Las entradas son emitidas una por una por el administrador del salón. Si te corresponde un acceso, vas a recibir tu invitación directamente por email.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <>
              <div className="space-y-4 mb-10">
                {event.ticketTypes && event.ticketTypes.length > 0 ? (
                  // --- NEW MULTI-TICKET UI ---
                  <div className="space-y-3">
                    {event.ticketTypes.map((ticket) => {
                      const sold = ticket.soldQuantity || 0;
                      const total = ticket.totalQuantity;
                      const availableCount = Math.max(0, total - sold);
                      const isSoldOut = availableCount === 0;
                      const isSelected = selectedTicketType?._id === ticket._id;

                      const pricing = getTicketPricing(ticket.price);

                      return (
                        <div
                          key={ticket._id}
                          onClick={() => !isSoldOut && setSelectedTicketType(ticket)}
                          className={`
                            relative border rounded-[24px] p-5 transition-all cursor-pointer
                            ${isSoldOut ? 'bg-white/5 border-white/5 opacity-50 cursor-not-allowed' :
                              isSelected
                                ? 'bg-primary-orange/10 border-primary-orange shadow-[0_0_30px_-10px_rgba(255,107,0,0.3)]'
                                : 'bg-white/[0.03] border-white/10 hover:bg-white/[0.06]'
                            }
                          `}
                        >
                          <div className="flex justify-between items-center gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`text-lg font-black ${isSelected ? 'text-primary-orange' : 'text-white'}`}>
                                  {ticket.name}
                                </span>
                                {ticket.ticketsIncluded > 1 && (
                                  <span className="px-2 py-0.5 rounded-md bg-purple-500/20 text-purple-300 text-[9px] font-black uppercase tracking-widest border border-purple-500/30">
                                    Pack {ticket.ticketsIncluded}x1
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-white/50 font-medium line-clamp-1 mb-2">
                                {ticket.description || (ticket.ticketsIncluded > 1 ? 'Pack de entradas' : 'Acceso al evento')}
                              </p>

                              <div className="flex items-baseline gap-2">
                                <span className="text-2xl font-black text-white">
                                  ${pricing.unitPrice.toLocaleString()}
                                </span>
                                {pricing.customerPaysServiceCharge && (
                                  <span className="text-[10px] text-white/30 uppercase tracking-widest">
                                    Incluye ${pricing.serviceChargeAmount.toLocaleString()} de cargo por servicio
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="flex flex-col items-end gap-2">
                              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${isSelected ? 'border-primary-orange bg-primary-orange' : 'border-white/20'}`}>
                                {isSelected && <div className="w-2.5 h-2.5 bg-black rounded-full" />}
                              </div>
                              <span className={`text-[10px] font-black uppercase tracking-widest ${isSoldOut ? 'text-red-400' : 'text-green-400'}`}>
                                {isSoldOut ? 'AGOTADO' : `${availableCount} DISPONIBLES`}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  // --- LEGACY SINGLE TICKET UI ---
                  <div className="group bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 rounded-[32px] p-6 transition-all duration-300">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-lg font-black text-white">General</span>
                          <span className={`px-2 py-0.5 rounded-md text-[8px] font-black uppercase tracking-widest ${isPast ? 'bg-white/5 text-white/20' : 'bg-green-500/20 text-green-400'}`}>
                            {isPast ? 'No Disponible' : `${Math.max(0, (event.totalTickets || 0) - (event.soldTickets || 0))} Disponibles`}
                          </span>
                        </div>
                        <p className="text-xs text-white/40 font-medium">Acceso general para mayores de 18 años.</p>

                        <div className="mt-4 space-y-1">
                          <p className="text-3xl font-black text-primary-orange leading-none">
                            ${getTicketPricing(event.price).unitPrice.toLocaleString()}
                          </p>
                          {getTicketPricing(event.price).customerPaysServiceCharge && (
                            <p className="text-[10px] text-white/30 font-bold uppercase tracking-widest">
                              Incluye cargo por servicio de ${getTicketPricing(event.price).serviceChargeAmount.toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Quantity Selector - Shared for both modes */}
                <div className="mt-6 flex items-center justify-between bg-white/[0.03] p-4 rounded-2xl border border-white/5">
                  <span className="text-sm font-bold text-white/50 uppercase tracking-widest">Cantidad</span>
                  <div className={`flex items-center bg-black/40 p-1.5 rounded-xl border border-white/5 shadow-inner ${isPast || (selectedTicketType && (selectedTicketType.totalQuantity - selectedTicketType.soldQuantity) <= 0) ? 'opacity-20 pointer-events-none' : ''}`}>
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      disabled={isPast}
                      className="w-10 h-10 rounded-lg flex items-center justify-center text-white/40 hover:text-white hover:bg-white/5 transition-all text-xl font-light"
                    >
                      −
                    </button>
                    <span className="font-black text-lg w-10 text-center text-white">{quantity}</span>
                    <button
                      onClick={() => {
                        let available = 0;
                        if (selectedTicketType) {
                          available = selectedTicketType.totalQuantity - selectedTicketType.soldQuantity;
                        } else {
                          available = (event.totalTickets || 0) - (event.soldTickets || 0);
                        }

                        if (quantity < available) setQuantity(quantity + 1);
                      }}
                      disabled={isPast}
                      className="w-10 h-10 rounded-lg flex items-center justify-center text-primary-orange hover:bg-primary-orange/10 transition-all text-xl font-light"
                    >
                      +
                    </button>
                  </div>
                </div>

              </div>

              {/* Botón Compra (Desktop) */}
              <div className="hidden md:block">
                <button
                  onClick={handlePurchase}
                  className={`group relative overflow-hidden w-full py-6 rounded-2xl font-black text-xl transition-all duration-500 shadow-2xl active:scale-[0.98] ${ticketState.loading || redirecting || isPast
                    ? "bg-white/5 text-white/20 cursor-not-allowed"
                    : "bg-brand-gradient text-white hover:shadow-orange-500/40"
                    }`}
                  disabled={ticketState.loading || redirecting || isPast}
                >
                  <div className="relative z-10 flex items-center justify-center gap-4">
                    {ticketState.loading || redirecting ? (
                      <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                    ) : isPast ? (
                      "EVENTO FINALIZADO"
                    ) : (
                      <>
                        {auth.isAuthenticated ? "COMPRAR AHORA" : "INICIAR SESIÓN PARA COMPRAR"}
                        <ChevronRight className="group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </div>
                  {redirecting && (
                    <div className="absolute inset-x-0 bottom-0 h-1 bg-white/20">
                      <div className="h-full bg-white transition-all duration-1000" style={{ width: `${(3 - countdown) * 33}%` }} />
                    </div>
                  )}
                </button>
                <div className="mt-4 flex items-center justify-center gap-3 text-white/30">
                  <ShieldCheck size={14} />
                  <p className="text-[10px] font-bold uppercase tracking-widest">Pago seguro vía Mercado Pago</p>
                </div>
              </div>
                </>
              )}
            </div>

            {/* TAB SYSTEM */}
            <div className="space-y-6">
              <div className="flex border-b border-white/5 overflow-x-auto gap-8 no-scrollbar">
                {[
                  { id: 'detalles', label: 'Información', icon: <Info size={16} /> },
                  { id: 'ubicacion', label: 'Cómo llegar', icon: <MapPin size={16} /> },
                  { id: 'productora', label: 'Productora', icon: <Store size={16} /> }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-4 flex items-center gap-2 text-xs font-black uppercase tracking-widest transition-all relative border-b-2 ${activeTab === tab.id
                      ? 'text-primary-orange border-primary-orange'
                      : 'text-white/40 border-transparent hover:text-white'
                      }`}
                  >
                    {tab.icon}
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="min-h-[200px] animate-in fade-in slide-in-from-bottom-2 duration-500">
                {activeTab === 'detalles' && (
                  <div className="prose prose-invert max-w-none">
                    <p className="text-white/70 leading-[1.8] text-lg font-medium whitespace-pre-wrap">
                      {event.description}
                    </p>
                  </div>
                )}

                {activeTab === 'ubicacion' && (
                  <div className="space-y-6">
                    <div className="bg-white/5 border border-white/10 rounded-[32px] p-6 flex flex-col md:flex-row items-center justify-between gap-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-primary-orange/10 rounded-2xl flex items-center justify-center text-primary-orange shrink-0">
                          <Navigation size={24} />
                        </div>
                        <div>
                          <p className="text-[10px] font-bold text-white/30 uppercase tracking-[2px] mb-1">Dirección del Evento</p>
                          <p className="text-white font-bold text-xl">{fullAddress}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`, '_blank')}
                        className="flex items-center gap-2 text-primary-orange font-bold text-sm hover:underline"
                      >
                        Abrir en Google Maps
                        <ExternalLink size={16} />
                      </button>
                    </div>

                    <div className="w-full h-[400px] rounded-[32px] overflow-hidden border border-white/10 shadow-2xl">
                      <iframe
                        title="Ubicación del evento"
                        width="100%"
                        height="100%"
                        style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg) brightness(0.9)' }}
                        src={googleMapsUrl || fallbackMapsUrl}
                        allowFullScreen
                      />
                    </div>
                  </div>
                )}

                {activeTab === 'productora' && (
                  <div className="bg-surface/50 backdrop-blur-md rounded-[32px] border border-white/5 p-8 flex flex-col md:flex-row items-center gap-8">
                    <div className="w-24 h-24 rounded-3xl bg-white/10 p-4 border border-white/10 flex items-center justify-center text-white/20">
                      {event.clientId?.logoUrl ? (
                        <img src={event.clientId.logoUrl} alt={event.clientId.nombre} className="w-full h-full object-contain" />
                      ) : (
                        <Store size={48} />
                      )}
                    </div>
                    <div className="flex-1 text-center md:text-left">
                      <h4 className="text-2xl font-black text-white mb-2">{event.clientId?.nombre || "Organización"}</h4>
                      <p className="text-white/40 text-sm leading-relaxed mb-6">
                        {event.clientId?.description || "Esta productora utiliza Passar para gestionar sus eventos con la mayor seguridad y transparencia posible."}
                      </p>
                      <button
                        onClick={() => navigate(`/${slug}`)}
                        className="px-6 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-xs font-bold hover:bg-white/10 transition-all active:scale-95"
                      >
                        Ver perfil organizacional
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* STICKY MOBILE CTA (Sólo mobile) */}
      {!isInvitationOnly && (
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 p-4 bg-background/80 backdrop-blur-2xl border-t border-white/5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-[10px] font-bold text-white/30 uppercase tracking-[2px]">Total</p>
            <p className="text-2xl font-black text-primary-orange leading-none">
              ${getTicketPricing(selectedTicketType?.price ?? event.price, quantity).totalAmount.toLocaleString()}
            </p>
          </div>
          <div className="flex items-center bg-white/5 p-1 rounded-2xl border border-white/10">
            <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-10 h-10 flex items-center justify-center text-white/40 text-lg">−</button>
            <span className="font-bold text-sm w-8 text-center text-white">{quantity}</span>
            <button onClick={() => {
              const available = (event.totalTickets || 0) - (event.soldTickets || 0);
              if (quantity < available) setQuantity(quantity + 1);
            }} className="w-10 h-10 flex items-center justify-center text-primary-orange text-lg">+</button>
          </div>
        </div>
        <button
          onClick={handlePurchase}
          className={`w-full py-5 rounded-2xl font-black text-lg transition-all duration-300 shadow-xl active:scale-[0.98] ${ticketState.loading || redirecting || isPast
            ? "bg-white/5 text-white/20 cursor-not-allowed"
            : "bg-brand-gradient text-white"
            }`}
          disabled={ticketState.loading || redirecting || isPast}
        >
          {isPast ? "EVENTO FINALIZADO" : auth.isAuthenticated ? (
            ticketState.loading ? "PROCESANDO..." : redirecting ? `ESPERÁ ${countdown}s` : "COMPRAR AHORA"
          ) : "INICIAR SESIÓN"}
        </button>
      </div>
      )}
    </div>
  );
};

export default EventDetails;
